import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Subject, Observable } from "rxjs";
import { Router } from "@angular/router";

import { environment } from "src/environments/environment";

const BACKEND_URL = environment.apiUrl + "/orders";
const TRANSACTIONS_URL = environment.apiUrl + "/transactions";

/** תשובה שלילית: חסרה, או success/ok=false, או שדה error טקסטואלי */
export function isPrintNodeStatusNegativeResponse(res: any): boolean {
  if (res == null) return true;
  if (typeof res === "object" && res !== null) {
    if (res.success === false || res.ok === false) return true;
    if (typeof res.error === "string" && res.error.length > 0) return true;
  }
  return false;
}

function isUnusableHttpErrorText(s: string): boolean {
  const t = s.trim();
  if (!t.length) return true;
  if (t.startsWith("<!DOCTYPE") || t.startsWith("<html") || /<html[\s>]/i.test(t)) return true;
  if (t.length > 280) return true;
  if (/cannot get\//i.test(t)) return true;
  return false;
}

function isPrintNodeFileEntry(v: unknown): v is Record<string, unknown> {
  return v !== null && typeof v === "object" && !Array.isArray(v) && ("printJobData" in (v as object) || "fileName" in (v as object));
}

function lastStateDetailMessage(stateData: unknown): string | null {
  if (!Array.isArray(stateData) || stateData.length === 0) return null;
  const inner = stateData[0];
  if (!Array.isArray(inner) || inner.length === 0) return null;
  const last = inner[inner.length - 1] as { message?: string };
  const m = typeof last?.message === "string" ? last.message.trim() : "";
  return m.length ? m : null;
}

export type PrintNodeJobOutcome = "done" | "error" | "progress" | "unknown";

export interface PrintNodeStatusDialogPrinterRow {
  displayName: string;
  printerStateRaw: string;
  computerStateRaw: string;
  hostname: string;
  /** מדפסת online ומחשב PrintNode מחובר */
  healthy: boolean;
}

export interface PrintNodeStatusDialogFileRow {
  fileKey: string;
  fileName: string;
  remoteId: string | null;
  jobStateRaw: string;
  outcome: PrintNodeJobOutcome;
  lastDetail: string | null;
}

export interface PrintNodeStatusDialogViewModel {
  isEmpty: boolean;
  printer: PrintNodeStatusDialogPrinterRow | null;
  files: PrintNodeStatusDialogFileRow[];
}

function printNodeJobOutcome(jobStateRaw: string): PrintNodeJobOutcome {
  const s = (jobStateRaw || "").toLowerCase();
  if (s === "done") return "done";
  if (s === "error" || s === "expired" || s === "cancelled") return "error";
  if (["queued", "in_progress", "new", "sent_to_client"].includes(s)) return "progress";
  return "unknown";
}

/** מודל תצוגה לדיאלוג סטטוס PrintNode מתשובת השרת */
export function parsePrintNodeStatusForDialog(res: any): PrintNodeStatusDialogViewModel {
  const empty: PrintNodeStatusDialogViewModel = { isEmpty: true, printer: null, files: [] };
  if (res == null || typeof res !== "object" || Array.isArray(res)) {
    return empty;
  }

  const entries = Object.entries(res).filter(([, v]) => isPrintNodeFileEntry(v)) as [string, Record<string, unknown>][];
  if (entries.length === 0) {
    return empty;
  }

  let printer0: Record<string, unknown> | undefined;
  for (const [, d] of entries) {
    const pjd = d["printJobData"] as Record<string, unknown> | undefined;
    const pr = pjd?.["printer"] as Record<string, unknown> | undefined;
    if (pr) {
      printer0 = pr;
      break;
    }
  }

  let printer: PrintNodeStatusDialogPrinterRow | null = null;
  if (printer0) {
    const displayName = String(printer0["name"] || printer0["description"] || "").trim();
    const pRaw = typeof printer0["state"] === "string" ? (printer0["state"] as string) : "";
    const comp = printer0["computer"] as Record<string, unknown> | undefined;
    const cRaw = comp && typeof comp["state"] === "string" ? (comp["state"] as string) : "";
    const hostname = String(comp?.["hostname"] || comp?.["name"] || "").trim();
    const healthy =
      pRaw.toLowerCase() === "online" && cRaw.toLowerCase() === "connected";
    printer = {
      displayName: displayName || "—",
      printerStateRaw: pRaw,
      computerStateRaw: cRaw,
      hostname,
      healthy,
    };
  }

  const files: PrintNodeStatusDialogFileRow[] = [];
  for (const [fileKey, d] of entries) {
    const fileName = String(d["fileName"] || "").trim() || fileKey;
    const pjd = d["printJobData"] as Record<string, unknown> | undefined;
    const jobRaw = typeof pjd?.["state"] === "string" ? (pjd["state"] as string) : "";
    const rid = d["remote_id"];
    const remoteId = rid != null && String(rid).length ? String(rid) : null;
    let lastDetail = lastStateDetailMessage(d["stateData"]);
    if (lastDetail && lastDetail.length > 220) {
      lastDetail = lastDetail.slice(0, 217) + "…";
    }
    if (jobRaw === "done") {
      lastDetail = null;
    }
    files.push({
      fileKey,
      fileName,
      remoteId,
      jobStateRaw: jobRaw || "—",
      outcome: printNodeJobOutcome(jobRaw),
      lastDetail,
    });
  }

  return { isEmpty: false, printer, files };
}

/** הודעה ל-snackbar משגיאת HTTP; לא מחזיר HTML גולמי מדפי שגיאה של Express וכו׳ */
export function messageForPrintNodeHttpError(
  err: any,
  fallback: string,
  notFoundFallback?: string
): string {
  const status = err?.status;
  const body = err?.error;
  const useNotFound = status === 404 && typeof notFoundFallback === "string" && notFoundFallback.length > 0;

  if (typeof body === "string") {
    if (isUnusableHttpErrorText(body)) {
      return useNotFound ? notFoundFallback : fallback;
    }
    return body.trim();
  }
  if (body && typeof body === "object" && typeof body.message === "string") {
    const m = body.message.trim();
    if (m.length && !m.includes("<") && m.length <= 280) {
      return m;
    }
  }
  return useNotFound ? notFoundFallback : fallback;
}

@Injectable({ providedIn: "root" })
export class OrdersService {
  orders: any[] = [];
  private ordersUpdated = new Subject<{ orders: any[], orderCount: number }>();
  numOfPendingOrders: number = 0;
  numOfPoints: number = 0;
  private numOfPendingOrdersUpdated = new Subject<{ numOfPendingOrders: number }>();
  private numOfPointsUpdated = new Subject<{ numOfPoints: number }>();
  private orderDeleted = new Subject<void>();
  private reportUpdated = new Subject<{ data: any }>();

  constructor(private http: HttpClient, private router: Router) { }

  getOrders(
    ordersPerPage: number,
    currentPage: number,
    userId: string = null,
    isPHOrdered: boolean = false
  ) {
    let id;
    if (userId) {
      id = userId;
    } else {
      id = localStorage.getItem('userId');
    }
    const PHText = isPHOrdered ? "true" : "false";
    const queryParams = `?pagesize=${ordersPerPage}&page=${currentPage}&ph=${PHText}`;
    const url = `${BACKEND_URL}/myorders/${id}` + queryParams;
    this.http
      .get<{ message: string, orders: any, maxOrders: number }>(url)
      .subscribe({
        next: (orderData) => {
          if (!orderData.orders) {
            return;
          }
          this.orders = orderData.orders;
          this.ordersUpdated.next({
            orders: [...this.orders],
            orderCount: orderData.maxOrders
          });
        },
        error: (error) => {
          console.error('Error fetching orders:', error);
          console.error('Request URL:', url);
          console.error('Error status:', error.status);
          console.error('Error message:', error.message);
          console.error('Error body:', error.error);
          // Emit empty orders to prevent UI from hanging
          this.ordersUpdated.next({
            orders: [],
            orderCount: 0
          });
        }
      });
  }

  /** למנהל (SU / BMPH) – צפייה בהזמנות של משתמש אחר */
  getOrdersForSu(
    ordersPerPage: number,
    currentPage: number,
    userId: string,
    isPHOrdered: boolean = false
  ) {
    const PHText = isPHOrdered ? "true" : "false";
    const queryParams = `?pagesize=${ordersPerPage}&page=${currentPage}&ph=${PHText}`;
    const url = `${BACKEND_URL}/userorders/${userId}` + queryParams;
    this.http
      .get<{ message: string, orders: any, maxOrders: number }>(url)
      .subscribe({
        next: (orderData) => {
          if (!orderData.orders) return;
          this.orders = orderData.orders;
          this.ordersUpdated.next({
            orders: [...this.orders],
            orderCount: orderData.maxOrders
          });
        },
        error: (error) => {
          console.error('Error fetching orders for SU:', error);
          this.ordersUpdated.next({ orders: [], orderCount: 0 });
        }
      });
  }

  getNumOfPendingOrders() {
    const userId = localStorage.getItem('userId');
    if (!userId) {
      return;
    }
    this.http
      .get<{ message: string; numOfPendingOrders: number; numOfPoints: number }>(`${BACKEND_URL}/numofpendingorders/${userId}`)
      .subscribe({
        next: (orderData) => {
          if ((!orderData.numOfPendingOrders && orderData.numOfPendingOrders !== 0) ||
            (!orderData.numOfPoints && orderData.numOfPoints !== 0)) {
            return;
          }
          this.numOfPendingOrders = orderData.numOfPendingOrders;
          this.numOfPoints = orderData.numOfPoints;
          this.numOfPendingOrdersUpdated.next({
            numOfPendingOrders: this.numOfPendingOrders,
          });
          this.numOfPointsUpdated.next({
            numOfPoints: this.numOfPoints,
          });
        },
        error: (err) => {
          console.log('numOfPendingOrders fetch error', err);
        },
      });
  }

  getPendingOrder(service: string, orderId: string):
    Observable<{
      message: string;
      orderData: any;
    }> {
    // console.log('getPendingOrder is called with:', service, orderId);
    let serviceName;
    if (service === 'p') {
      serviceName = 'plotter';
    } else if (service === 'e') {
      serviceName = 'express';
    }
    return this.http.get<{
      message: string;
      orderData: any;
    }>(`${BACKEND_URL}/pendingorder/${serviceName}/${orderId}`);
  }

  getOrderUpdateListener() {
    return this.ordersUpdated.asObservable();
  }

  getNumOfPendingOrdersUpdateListener() {
    return this.numOfPendingOrdersUpdated.asObservable();
  }

  getNumOfPointsUpdateListener() {
    return this.numOfPointsUpdated.asObservable();
  }

  notifyOrderUpdated() {
    this.orderDeleted.next();
  }

  getOrderDeletedListener() {
    return this.orderDeleted.asObservable();
  }

  deleteOrder(
    service: string,
    userId: string,
    orderId: string,
    isSu: boolean = false
  ): Observable<any> {
    if (!isSu) {
      // user
      return this.http
        .put(`${BACKEND_URL}/deleteorder/${service}/${orderId}`, userId);
    } else {
      // su
      return this.http
        .put(`${BACKEND_URL}/deleteordersu/${service}/${orderId}`, userId);
    }
  }

  getOrdersForManager(service: string, branchId: string, ordersPerPage: number, currentPage: number) {
    const requestBody = { service, branchId, ordersPerPage, currentPage };
    const backendUrl = service !== 'ph' ? `${BACKEND_URL}/ordersformanager/${service}/${branchId}` : `${environment.apiUrl}/ph/ordersformanager`;
    this.http.post<{ message: string; orders: any, maxOrders: number }>(backendUrl, requestBody)
      .subscribe(orderData => {
        // console.log('Response from server:', orderData);
        if (!orderData.orders) {
          return;
        }
        this.orders = orderData.orders;
        // console.log('orders:', this.orders);
        this.ordersUpdated.next({
          orders: [...this.orders],
          orderCount: orderData.maxOrders
        });
      });
  }

  getReportData(
    service: string,
    branchId: string,
    month: number,
    year: number
  ) {
    const requestBody = { service, branchId, month, year };
    this.http.post<{ message: string, reportData: any }>(`${BACKEND_URL}/reportdata/${service}/${branchId}/${month}/${year}`, requestBody)
      .subscribe((response) => {
        this.reportUpdated.next({ data: response.reportData });
      });
  }

  getReportDataUpdateListener(): Observable<{ data: any }> {
    return this.reportUpdated.asObservable();
  }

  /** שרת אקספרס (כמו /api/orders/print) — baseUrl ללא סלאש בסוף */
  getPrintNodeStatus(expressServerBaseUrl: string, orderId: string): Observable<any> {
    const base = expressServerBaseUrl.replace(/\/$/, "");
    return this.http.get(`${base}/api/orders/${orderId}/printnode-status`);
  }

  /** SU — עד 10 עסקאות אחרונות להזמנה ולמשתמש */
  getTransactionsByOrder(
    userId: string,
    orderId: string
  ): Observable<{ message: string; transactions: any[] }> {
    return this.http.post<{ message: string; transactions: any[] }>(
      `${TRANSACTIONS_URL}/transactionsbyorder`,
      { userId, orderId }
    );
  }

  /** SU — עד 10 שגיאות חיוב אחרונות של משתמש (כל ההזמנות) */
  getTransactionsByUser(
    userId: string
  ): Observable<{ message: string; transactions: any[] }> {
    return this.http.post<{ message: string; transactions: any[] }>(
      `${TRANSACTIONS_URL}/transactionsbyuser`,
      { userId }
    );
  }
  // ====================
}
